import { documentFetch } from "@/lib/document/docClient";
import type { DocAsset, DocAssetListResponse, DocAssetType } from "@/lib/document/assetTypes";
import { requireSupabase } from "@/lib/supabase";

const LIST_PATH = "/api/doc/asset-list";
const SIGN_PATH = "/api/doc/asset-sign";
const COMMIT_PATH = "/api/doc/asset-commit";
const SYNC_ORG_LOGO_PATH = "/api/doc/asset-sync-org-logo";

type SignOk = {
  ok: true;
  bucket: string;
  path: string;
  token: string;
  signed_url: string;
  asset_id: string;
  asset_type: DocAssetType;
  content_type: string;
};

type CommitOk = {
  ok: true;
  asset: DocAsset;
};

function isSignOk(x: unknown): x is SignOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    o.ok === true &&
    typeof o.bucket === "string" &&
    typeof o.path === "string" &&
    typeof o.token === "string" &&
    typeof o.asset_id === "string"
  );
}

function isCommitOk(x: unknown): x is CommitOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return o.ok === true && typeof o.asset === "object" && o.asset !== null;
}

export function listDocAssets(args?: { assetType?: DocAssetType; signMedia?: boolean }) {
  const q = new URLSearchParams();
  if (args?.assetType) q.set("asset_type", args.assetType);
  if (args?.signMedia) q.set("sign_media", "1");
  const suffix = q.toString();
  return documentFetch<DocAssetListResponse>(
    suffix ? `${LIST_PATH}?${suffix}` : LIST_PATH,
    { method: "GET" },
  );
}

export type UploadDocAssetResult =
  | { ok: true; asset: DocAsset }
  | { ok: false; error: string };

export async function uploadDocAsset(args: {
  file: File;
  assetType: DocAssetType;
  displayName: string;
  width?: number | null;
  height?: number | null;
}): Promise<UploadDocAssetResult> {
  const sb = requireSupabase();
  const contentType = (args.file.type || "image/jpeg").toLowerCase();
  const byteSize = args.file.size;
  if (byteSize <= 0) {
    return { ok: false, error: "File is empty." };
  }

  const sign = await documentFetch<unknown>(SIGN_PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      asset_type: args.assetType,
      display_name: args.displayName,
      content_type: contentType,
      byte_size: byteSize,
    }),
  });
  if (!sign.ok) return { ok: false, error: sign.error };
  if (!isSignOk(sign.data)) {
    const err = (sign.data as { error?: unknown })?.error;
    return { ok: false, error: typeof err === "string" ? err : "Sign response invalid" };
  }
  const s = sign.data;

  const { error: upErr } = await sb.storage.from(s.bucket).uploadToSignedUrl(s.path, s.token, args.file, {
    contentType: s.content_type,
    upsert: false,
  });
  if (upErr) return { ok: false, error: upErr.message };

  const commit = await documentFetch<unknown>(COMMIT_PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      asset_id: s.asset_id,
      object_path: s.path,
      asset_type: args.assetType,
      display_name: args.displayName,
      content_type: s.content_type,
      byte_size: byteSize,
      width: args.width ?? null,
      height: args.height ?? null,
    }),
  });
  if (!commit.ok) return { ok: false, error: commit.error };
  if (!isCommitOk(commit.data)) {
    const err = (commit.data as { error?: unknown })?.error;
    return { ok: false, error: typeof err === "string" ? err : "Commit response invalid" };
  }
  return { ok: true, asset: commit.data.asset };
}

/** Import org branding logo URL into the document asset library (logo type). */
export async function syncOrgLogoToAssetLibrary(logoUrl?: string): Promise<
  { ok: true; created: boolean } | { ok: false; error: string }
> {
  const body = logoUrl?.trim() ? { logo_url: logoUrl.trim() } : {};
  const r = await documentFetch<{ ok: true; created: boolean }>(SYNC_ORG_LOGO_PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) return { ok: false, error: r.error };
  return { ok: true, created: r.data.created === true };
}

export async function readImageDimensions(
  file: File,
): Promise<{ width: number; height: number } | null> {
  if (!file.type.startsWith("image/")) return null;
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}
